/**
 * WhatsAppActionButton - Botó d'acció per enviar missatges WhatsApp (fitxa, `MessagingBadge`)
 *
 * El flux (crear tasca → previsualització → obrir WhatsApp → «enviat» en tornar) viu a
 * `useWhatsAppSendFlow`, compartit amb la fila Welcome · Codi · Trucar del popover
 * (`QuickWhatsAppActions`). Aquí només hi ha el botó.
 */

import { Button } from '@/components/ui/button';
import { MessageSquare, Loader2 } from 'lucide-react';
import type { MessageKind } from '@guilleumes/types';
import { cn } from '@/lib/utils';
import { useWhatsAppSendFlow } from './useWhatsAppSendFlow';

interface WhatsAppActionButtonProps {
  reservationId: string;
  messageKind: MessageKind;
  variant?: 'default' | 'secondary' | 'outline' | 'ghost' | 'link' | 'destructive';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  onSent?: () => void;
  disabled?: boolean;
  className?: string;
}

// Emoji per kind
const kindEmoji: Record<MessageKind, string> = {
  FIRST_CONTACT: '📩',
  WELCOME: '👋',
  GUEST_FORM: '📋',
  ACCESS: '🔑',
  PAYMENT_LINK: '💳',
  REVIEW: '⭐',
  CUSTOM: '✏️',
};

// Label per kind
const kindLabel: Record<MessageKind, string> = {
  FIRST_CONTACT: 'Primer contacte',
  WELCOME: 'Benvinguda',
  GUEST_FORM: 'Registre',
  ACCESS: 'Accés',
  PAYMENT_LINK: 'Pagament',
  REVIEW: 'Ressenya',
  CUSTOM: 'Personalitzat',
};

export function WhatsAppActionButton({
  reservationId,
  messageKind,
  variant = 'secondary',
  size = 'default',
  onSent,
  disabled,
  className,
}: WhatsAppActionButtonProps) {
  const { start, isCreating, dialogs } = useWhatsAppSendFlow({
    reservationId,
    messageKind,
    title: kindLabel[messageKind],
    onSent,
  });

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={start}
        disabled={disabled || isCreating}
        className={cn('gap-2', className)}
        // A4.5: a mòbil el text s'amaga i només queden les icones.
        aria-label={`WhatsApp: ${kindLabel[messageKind]}`}
        title={`WhatsApp: ${kindLabel[messageKind]}`}
      >
        {isCreating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <span>{kindEmoji[messageKind]}</span>
        )}
        <span className="hidden sm:inline">{kindLabel[messageKind]}</span>
        <MessageSquare className="h-4 w-4 sm:hidden" />
      </Button>

      {dialogs}
    </>
  );
}
